<template>
  <div v-if="quote" :class="['shortlist-item shortlist-item--large', (sideBySide) ? 'side-by-side' : null]">

    <div class="tabs-container" style="width:100%;">
      <ul class="tabs">
        <li class="tab"
          :class="{'active': (activeQuoteInGroup == index), 'selected': (quoteInGroup.quoteID === selectedQuote.quoteID)}"
          v-for="(quoteInGroup, index) in quotesInGroup">
          <a href="#" @click.prevent="viewQuoteInGroup(panel, index)" aria-label="View this quote"
            class="tab-link">{{ quoteInGroup.quoteNumber }} <span class="icon-selected"></span></a>
        </li>
        <li style="float:right;list-style:none;display:flex;align-items:center;height:50px;">
          <button class="btn btn-sm btn-danger-light me-2" @click.prevent="removeDerivative()">
            <i class="icon-cross"></i> <span>Remove all quotes for vehicle</span>
          </button>
          <button class="btn btn-sm btn-danger-light" @click="remove(quote)">
            <i class="icon-cross"></i> Remove Quote
          </button>
        </li>
      </ul>
    </div>

    <side-by-side
      v-if="sideBySide"
      :quote="quote"
      :averagePencePerMile="averagePencePerMile"
      :showMpgSliderIfApplicable="showMpgSliderIfApplicable"
      :fuelTypeElectricOrPluginHybrid="fuelTypeElectricOrPluginHybrid"
      :driverGradesById="driverGradesById"
    />
    <single-vehicle
      v-else
      :quote="quote"
      :averagePencePerMile="averagePencePerMile"
      :showMpgSliderIfApplicable="showMpgSliderIfApplicable"
      :fuelTypeElectricOrPluginHybrid="fuelTypeElectricOrPluginHybrid"
      :driverGradesById="driverGradesById"
    />

    <div class="column column-three">
      <div class="inner-tabs d-flex">
        <a href="#" aria-label="View Monthly Costs" class="flex-fill"
          :class="{'active': visibleInnerTab === 'monthlyCosts'}"
          @click.prevent="viewInnerTab('monthlyCosts')">Monthly Cost</a>
        <a href="#" aria-label="View Selected Options" class="flex-fill"
          :class="{'active': visibleInnerTab === 'selectedOptions'}"
          @click.prevent="viewInnerTab('selectedOptions')">Selected Options</a>
      </div>
      <div class="inner-tab-content">
        <template v-if="quoteExpired">
          <div class="pt-5 text-center">
            <h5>This quote has expired</h5>
            <a href="#" @click="refreshJaamaQuote(quote.quoteID)">Click here to generate a new quote</a>
          </div>
        </template>
        <template v-else>
          <template v-if="visibleInnerTab === 'monthlyCosts'">
            <h6 class="mb-4">Figures are based on a Tax Rate of {{ taxRate }}%</h6>
            <div v-if="quote.quote && adminPortalData">
              <ecoamaps-quote-breakdown v-if="scheme === 'ecoamaps'"
                :quote="quote.quote"
                :quoteCosts="costsForSalarySacrificeQuote"
                :averagePencePerMile="averagePencePerMile"
                :totalEstimatedElectricCostPerMonth="totalEstimatedElectricCostPerMonth"
                :driver-profile="driverProfile"
                :type="shortlist.type"
                :show-fuel="true"
                :show-traditional-cost="true"/>
              <salsac-quote-breakdown v-if="scheme === 'salsac'"
                :quote="quote.quote"
                :quoteCosts="costsForSalarySacrificeQuote"
                :averagePencePerMile="averagePencePerMile"
                :totalEstimatedElectricCostPerMonth="totalEstimatedElectricCostPerMonth"
                :driver-profile="driverProfile"
                :type="shortlist.type"
                :show-fuel="true"
                :show-traditional-cost="true"/>
              <bch-quote-breakdown v-if="scheme === 'bch' && driverProfile"
                :quote="quote.quote"
                :derivative="quote.derivative"
                :averagePencePerMile="averagePencePerMile"
                :totalEstimatedElectricCostPerMonth="totalEstimatedElectricCostPerMonth"
                :driverProfile="driverProfile"
                :type="shortlist.type"
                :adminPortalData="adminPortalData"
              />
            </div>

          </template>
          <template v-if="visibleInnerTab === 'selectedOptions'">
            <div class="selected-options">
              <h6 class="mb-2">Your current additions</h6>
              <ul>
                <template v-for="optionGroup in quote.quote.factoryOptionsGrouped">
                  <template v-for="option in optionGroup">
                    <li v-if="!option.isDefault">{{ option.description }}</li>
                  </template>
                </template>
              </ul>
            </div>
          </template>

          <ul class="actions">
            <li>
              <button
                @click="$router.push({ name: 'Configure Vehicle', params: { shortListId: shortlist.shortListID, quoteId: quote.quoteID }})"
                class="button button--charcoal-ghost button--small reconfigure">
                <template v-if="shortlistItemConfigured(quote)">
                  Reconfigure
                </template>
                <template v-else>Configure</template>
              </button>
            </li>
            <li>
              <button @click="selectQuote(shortlist.type, quote)"
                :class="['button button--charcoal button--small', (quote.quoteID === selectedQuote.quoteID) ? 'selected' : null, cannotSelectVehicle ? 'button--disabled' : '']"
                :disabled="cannotSelectVehicle"
              >
                <template v-if="ratebookEnabled && oocRemaining && oocRemaining < 0">
                  One Off Contribution Exceeds Maximum Allowed
                </template>
                <template v-else-if="ratebookEnabled && !netImpactMode && wlcRemaining && wlcRemaining < 0">
                  Whole Life Cost Exceeds Maximum Allowed
                </template>
                <template v-else-if="netImpactTradeupExceeded">
                  Maximum Trade Up Exceeded
                </template>
                <template v-else-if="!this.configured">
                  Please select your configuration
                </template>
                <template v-else-if="quote.quoteID === selectedQuote.quoteID">
                  Selected
                  <span class="icon-selected"></span>
                </template>
                <template v-else>Select</template>
              </button>
            </li>
          </ul>
        </template>
      </div>
    </div>

    <div class="column column-four">
      <div class="shortlist-controls" v-if="shortlist.quotes.length">
        <ul class="controls">
          <li class="controls__previous">
            <button @click.prevent="viewPreviousQuoteGroup"><i class="icon-arrow-right-1"></i></button>
          </li>
          <li class="controls__count">{{ currentQuoteGroup + 1 }}/{{ shortlist.quotes.length }}</li>
          <li class="controls__next">
            <button @click.prevent="viewNextQuoteGroup"><i class="icon-arrow-right-1"></i></button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import EstimatedElectricCosts from "@/components/car-comparison/estimated-electric-costs";
import MpgSlider from "@/components/car-comparison/mpg-slider";
import EcoamapsQuoteBreakdown from "@/components/car-quote/ecoamaps/quote-breakdown";
import SalsacQuoteBreakdown from "@/components/car-quote/salsac/quote-breakdown";
import BchQuoteBreakdown from "@/components/car-quote/bch/quote-breakdown";
import AdditionalData from '@/components/car-comparison/comparison-additional';
import VehicleImage from "@/components/vehicle-details/vehicle-image";
import ComparisonAdditional from "@/components/car-comparison/comparison-additional";
import VehicleMeta from "@/components/car-comparison/vehicle-meta.vue";
import SideBySide from "@/components/car-comparison/layout/side-by-side.vue";
import SingleVehicle from "@/components/car-comparison/layout/single-vehicle.vue";
import {fuelTypeElectric, fuelTypeElectricOrPluginHybrid} from "@/helpers/fuel-type";
import RatebookHelper from "@/helpers/ratebook";
import BikPlusNetImpact from "@/helpers/net-cost/bik-plus-net-impact";
import {useRoute} from "vue-router";
import {EventBus} from "@/event-bus";

export default {
  components: {
    SingleVehicle,
    SideBySide,
    VehicleMeta,
    BchQuoteBreakdown,
    AdditionalData,
    EcoamapsQuoteBreakdown,
    SalsacQuoteBreakdown,
    MpgSlider,
    EstimatedElectricCosts,
    VehicleImage,
    ComparisonAdditional,
  },
  props: [
    'adminPortalData',
    "panel",
    "sideBySide",
    "classes",
    "quote",
    "quoteExpired",
    "refreshJaamaQuote",
    "shortlist",
    "session",
    "selectedQuote",
    "viewPreviousQuoteGroup",
    "viewNextQuoteGroup",
    "currentQuoteGroup",
    "activeQuoteInGroup",
    "costsForSalarySacrificeQuote",
    "averagePencePerMile",
    "totalEstimatedElectricCostPerMonth",
    "remove",
    "viewQuoteInGroup",
    "selectQuote",
    'driverGradesById',
  ],

  data: () => {
    return {
      defaultMpg: null,
      supportedTerms: [24, 36, 48],
      visibleInnerTab: 'monthlyCosts',
      quoteCosts: null
    }
  },

  setup() {
    const route = useRoute();
    return { route }
  },

  mounted() {
    this.defaultMpg = (this.quote && this.quote.derivedMPG ? this.quote.derivedMPG : 30);
  },

  computed: {
    RatebookHelper() {
      return RatebookHelper
    },
    // A basic, non-definitive way of checking if a user has a ratebook for this vehicle, and thus should see the ratebook values
    ratebook() {
      return this.quote.quote.taxCalculations;
    },
    ratebookEnabled() {
      return this.RatebookHelper.isRatebookEnabledGrade(this.quote.quote.details.driverGradeName);
    },
    term() {
      return this.quote.quote.definition.plan.term;
    },
    driverGrade() {
      return this.quote.quote.driverGrade;
    },
    ratebookNotSatisfied() {
      if (!this.ratebookEnabled) {
        return false;
      }
      const oocExceeded = !!this.oocRemaining && this.oocRemaining < 0;
      // Net-impact mode allows going over the WLC limit (that is the trade-up); only the max trade-up blocks
      if (this.netImpactMode) {
        return oocExceeded || this.netImpactTradeupExceeded;
      }
      return oocExceeded || (!!this.wlcRemaining && this.wlcRemaining < 0);
    },
    netImpactMode() {
      return BikPlusNetImpact.isEnabled(this.adminPortalData?.content?.products?.bch?.[0]);
    },
    netImpactTradeupExceeded() {
      if (!this.netImpactMode || !this.ratebookEnabled) {
        return false;
      }
      const ratePercent = this.session?.driverProfile?.incomeTaxBand?.ratePercent;
      return BikPlusNetImpact.breakdown(this.ratebook, this.term, ratePercent, this.driverGrade).exceedsMaxTradeup;
    },
    oocRemaining() {
      return this.RatebookHelper.oneOffContributionRemaining(this.ratebook, this.driverGrade.deloitteFilters.employeeCostCapitalContributionTo, this.term);
    },
    wlcRemaining() {
      return this.RatebookHelper.wholeLifeCostRemaining(this.ratebook, this.driverGrade.deloitteFilters.companyCostTotalTo, this.term);
    },
    driverProfile() {
      return this.session.driverProfile
    },
    quotesInGroup() {
      return this.shortlist.quotes[this.currentQuoteGroup];
    },
    taxRate() {
      return this?.session?.driverProfile?.incomeTaxBand?.ratePercent;
    },
    isSalSac() {
      return (this.shortlist.name === 'salsac');
    },
    cannotSelectVehicle() {
      return (this.isSalSac && (!this.costsForSalarySacrificeQuote || this.costsForSalarySacrificeQuote === 'NOT_FOUND')) || !this.configured || this.ratebookNotSatisfied;
    },
    showMpgSliderIfApplicable() {
      return !this.shortlist.name === 'bch';
    },
    scheme() {
      return this.route.params.scheme;
    },
    configured() {
      return this.shortlistItemConfigured(this.quote);
    }
  },
  methods: {
    fuelTypeElectric,
    fuelTypeElectricOrPluginHybrid,
    viewInnerTab(tab) {
      this.visibleInnerTab = tab;
    },

    shortlistItemConfigured(item) {
      // If paintwork or trim aren't set, the vehicle isn't configured yet.
      return !(item.quote.factoryOptionsGrouped['Paintwork'] === undefined ||
        item.quote.factoryOptionsGrouped['Trim'] === undefined);
    },

    removeDerivative() {
      EventBus.$emit('remove-derivative', this.quote.derivativeID);
    },
  }
}
</script>
